// Inline "Recent activity" preview card for the contract Details tab.
// Shows the last 5 entries grouped by day. The "View all activity" link
// opens the ActivityLogDrawer for full history with filter support.

import { useState, useMemo } from "react";
import { ArrowUpRight } from "lucide-react";
import { Card } from "../../common";
import { useRecentContractActivity } from "../../../hooks/useContractActivity";
import ActivityLogItem from "./ActivityLogItem";
import ActivityLogDrawer from "./ActivityLogDrawer";
import { groupActivityByDay } from "./activityGrouping";

interface ActivityLogSectionProps {
  contractId: string;
}

export default function ActivityLogSection({
  contractId,
}: ActivityLogSectionProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data, isPending } = useRecentContractActivity(contractId, 5);

  const entries = data?.entries ?? [];
  const groups = useMemo(
    () => groupActivityByDay(data?.entries ?? []),
    [data?.entries],
  );

  return (
    <>
      <Card padding="md" data-testid="activity-log-section">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <h2 className="text-h4 text-slate-900 dark:text-slate-50">
              Recent activity
            </h2>
            {entries.length > 0 && (
              <p className="text-caption text-slate-500 dark:text-slate-400">
                Showing the {entries.length} most recent
                {entries.length === 1 ? " entry" : " entries"}
              </p>
            )}
          </div>
          {entries.length > 0 && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-1 text-caption text-brand-600 hover:text-brand-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400 dark:hover:text-brand-300"
            >
              View all activity
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>

        {isPending ? (
          /* Skeleton loading state */
          <div className="space-y-3" aria-busy="true" aria-label="Loading activity">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-6 w-6 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
                <div className="h-4 flex-1 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <p className="text-body-sm italic text-slate-500 dark:text-slate-400">
            No activity recorded yet — events will appear as the contract is
            edited.
          </p>
        ) : (
          <ol role="list" className="space-y-3">
            {groups.map((group) => (
              <li key={group.dayLabel}>
                <p className="mb-1 text-caption font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  {group.dayLabel}
                </p>
                {group.entries.map((entry, ei) => (
                  <ActivityLogItem
                    key={`${entry.occurredAt}#${ei}`}
                    entry={entry}
                  />
                ))}
              </li>
            ))}
          </ol>
        )}
      </Card>

      <ActivityLogDrawer
        contractId={contractId}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
}
